import { useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

interface cardFieldEditContextProps {
  activeId: string | undefined;
  mode: 'create' | 'update' | undefined;
  requestToMakeActive: (id: string, mode: 'create' | 'update') => void;
  makeInactive: (id?: string) => void;
}

/**
 * Enforce that there is only ever one instance of something available at any given time.
 * Great for any UI element that should only have a single instance on screen, e.g. a hover
 * popover.
 */
export default function useCardFieldEditContext(): [
  cardFieldEditContextProps,
  React.Dispatch<React.SetStateAction<string | undefined>>,
] {
  const [activeId, setActiveId] = useState<string>();
  const [mode, setMode] = useState<cardFieldEditContextProps['mode']>();
  const activeIdRef = useRef('');

  const handleRequestToMakeActive = useCallback((id: string, mode: 'create' | 'update') => {
    ReactDOM.unstable_batchedUpdates(() => {
      setActiveId(id);
      setMode(mode);
    });

    activeIdRef.current = id;
  }, []);

  const handleMakeInactive = useCallback((id?: string) => {
    // We use a ref here instead of state because we want to be sure that even if this is triggered in the same
    // event bubbling sequence that we are looking at the actual active element id, and not a piece of old state
    // due to closures.
    if (activeIdRef.current === id) {
      ReactDOM.unstable_batchedUpdates(() => {
        setActiveId(undefined);
        setMode(undefined);
      });
    }
  }, []);

  return [
    {
      activeId,
      mode,
      requestToMakeActive: handleRequestToMakeActive,
      makeInactive: handleMakeInactive,
    },
    setActiveId,
  ];
}
